import styled, { useTheme } from "styled-components";
import Text from "common/Typography/Text";
import CustomInputNumber from "common/Inputs/CustomInputNumber";
import Slider from "common/Slider/Slider";
import { CSSProperties } from "react";
import { useDebouncedValue } from "@shared/utils/hooks/useDebouncedValue";
import { typography } from "@shared/common/Themes/typography";
import { SliderAndInputProps as SharedSliderAndInputProps } from "@shared/features/bots/BotForm/common/types";
import { FormFieldTitle } from "features/bots/common/styles";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";

interface SliderAndInputProps extends SharedSliderAndInputProps {
  style?: CSSProperties;
}

const SliderAndInput = ({
  name,
  value,
  setValue,
  maxValue,
  minValue,
  step = 1,
  precision = 0,
  style,
  suffix,
  useDebounce,
  helperTooltip,
  showError = true,
  placeholder,
  isFetching,
  wait = 200,
  inputTestId,
}: SliderAndInputProps) => {
  const theme = useTheme();
  const [debouncedValue, debouncedOnChange, flushDebouncedValue] =
    useDebouncedValue(value, setValue, undefined, wait);
  const valueToUse = useDebounce ? debouncedValue : value;
  const onChangeToUse = useDebounce ? debouncedOnChange : setValue;
  const getErrorMessage = () => {
    if (!showError) return;
    if (valueToUse === undefined || valueToUse === "")
      return `You must provide a value for this parameter`;
    else if (parseFloat(valueToUse) === 0)
      return "Value should be greater than 0";
    else if (maxValue && parseFloat(valueToUse) > maxValue)
      return `Value should not exceed ${maxValue}`;
  };

  const error = getErrorMessage();

  const onSliderChange = (value: number) => {
    if (minValue && value < minValue) {
      onChangeToUse(minValue.toString());
    } else {
      onChangeToUse(value.toString());
    }
  };

  return (
    <Wrapper style={style}>
      <LoadingSpinnerOverlay
        isLoading={isFetching}
        backgroundColor={theme.colors.layoutBgColor}
      >
        {(!!name || !!helperTooltip) && (
          <FormFieldTitle>
            {name}
            {helperTooltip}
          </FormFieldTitle>
        )}
        <SliderAndInputWrapper>
          <SliderWrapper>
            <Slider
              value={valueToUse ? parseFloat(valueToUse) : 0}
              onChange={onSliderChange}
              onChangeComplete={flushDebouncedValue}
              max={maxValue}
              min={minValue}
              step={step}
              style={{ height: 16 }}
              tooltip={{
                placement: "bottom",
                formatter: (value) => `${value}${suffix || ""}`,
              }}
            />
          </SliderWrapper>
          <InputWrapper>
            <CustomInputNumber
              data-testid={inputTestId}
              placeholder={placeholder}
              bordered={true}
              value={valueToUse}
              onChange={onChangeToUse}
              onBlur={flushDebouncedValue}
              precision={precision}
              stringMode={true}
              style={{ height: 32 }}
              suffix={suffix}
            />
          </InputWrapper>
        </SliderAndInputWrapper>
        {error && (
          <Text type="danger" $size={typography.fontSizes.S}>
            {error}
          </Text>
        )}
      </LoadingSpinnerOverlay>
    </Wrapper>
  );
};

export default SliderAndInput;

const Wrapper = styled.div`
  margin-bottom: 8px;
  position: relative;
`;
const SliderWrapper = styled.div`
  flex: auto;
  padding-right: 16px;
`;
const InputWrapper = styled.div`
  flex: none;
  width: 85px;
`;
const SliderAndInputWrapper = styled.div`
  display: flex;
`;
